import type {
  Copilot,
  CopilotAssignmentRepository,
} from '@github-ui/agent-sessions/components/copilot-assignment/copilot-assignment-types'
import {CopilotAssignmentButton} from '@github-ui/agent-sessions/components/copilot-assignment/CopilotAssignmentButton'
import {CopilotAssignmentModal} from '@github-ui/agent-sessions/components/copilot-assignment/CopilotAssignmentModal'
import {
  CopilotCodingAgentRequestAccessDialog,
  HIDE_CCA_REQUEST_DIALOG_KEY,
} from '@github-ui/agent-sessions/components/copilot-assignment/CopilotCodingAgentRequestAccessDialog'
import {useCopilotCodingAgentEligibility} from '@github-ui/agent-sessions/hooks/use-copilot-coding-agent-eligibility'
import {Assignees} from '@github-ui/assignees'
import {isCopilot} from '@github-ui/assignees/copilot-user'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {commitUpdateIssueAssigneesMutation} from '@github-ui/item-picker/commitUpdateIssueAssigneesMutation'
import {DEFAULT_MAXIMUM_ASSIGNEES} from '@github-ui/item-picker-assignee'
import type {AssigneeIssuePickerProps, AssigneePickerAssignee} from '@github-ui/item-picker-assignee-relay'
import {AssigneePicker, AssigneeRepositoryPicker} from '@github-ui/item-picker-assignee-relay'
import {useViewer} from '@github-ui/item-picker-assignee-relay/useViewer'
import {ERRORS} from '@github-ui/item-picker-base/constants/errors'
import {noop} from '@github-ui/noop'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
// eslint-disable-next-line no-restricted-imports
import {useToastContext} from '@github-ui/toast/ToastContext'
import {useAnalytics} from '@github-ui/use-analytics'
import {useLocalStorage} from '@github-ui/use-safe-storage/local-storage'
import {Button} from '@primer/react'
import type {SxProp} from '@primer/styled-react'
import {type RefObject, useCallback, useMemo, useRef, useState} from 'react'
import {useRelayEnvironment} from 'react-relay'
import {commitLocalUpdate, graphql, useFragment} from 'react-relay/hooks'

import {LABELS} from '../../constants/labels'
import {TEST_IDS} from '../../constants/test-ids'
import {ReadonlySectionHeader} from '../ReadonlySectionHeader'
import {Section} from '../Section'
import {SectionHeader} from '../SectionHeader'
import type {AssigneesSectionAssignees$key} from './__generated__/AssigneesSectionAssignees.graphql'
import type {AssigneesSectionCopilotRepositoryFragment$key} from './__generated__/AssigneesSectionCopilotRepositoryFragment.graphql'
import type {AssigneesSectionFragment$key} from './__generated__/AssigneesSectionFragment.graphql'
import type {AssigneesSectionLazyFragment$key} from './__generated__/AssigneesSectionLazyFragment.graphql'
import type {AssigneesSectionViewer$key} from './__generated__/AssigneesSectionViewer.graphql'
import styles from './AssigneesSection.module.css'
import {addAssigneesToIssueLocally} from './relay-tsq-interop'

const ReadonlyAssigneesSectionHeader = () => <ReadonlySectionHeader title={LABELS.sectionTitles.assignees} />

// Helper function to compare two arrays of assignees by their IDs
const assigneesEqual = (a: AssigneePickerAssignee[], b: AssigneePickerAssignee[]): boolean => {
  if (a.length !== b.length) return false
  const aIds = new Set(a.map(assignee => assignee.id))
  return b.every(assignee => aIds.has(assignee.id))
}

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const AssigneesSectionLazyFragment = graphql`
  fragment AssigneesSectionLazyFragment on Issue {
    suggestedActors(first: 10) {
      nodes {
        ... on Actor {
          __typename
          id
          login
          name
          profileResourcePath
          avatarUrl(size: 64)
          ... on Bot {
            isCopilot
            isAgent
            displayName
          }
        }
      }
    }
    repository {
      viewerCopilotCodingAgentEligibility {
        hasRequestedCca
        canRequestCca
        canGrantOrgCca
        canGrantEnterpriseCca
      }
      owner {
        ... on Organization {
          enterprise {
            slug
          }
        }
      }
    }
  }
`

type AssigneesSectionProps = {
  sectionHeader: React.ReactElement
  onSelfAssignClick: () => void
  assignees: AssigneePickerAssignee[]
  readonly: boolean
  className?: string
  copilotAssignmentButton?: React.ReactElement
  copilotEligibility?: ReturnType<typeof useCopilotCodingAgentEligibility>
  owner?: string
  businessSlug?: string | null
  onEligibilityUpdate?: () => Promise<void>
  onRequestSuccess?: (actionType?: 'request' | 'enable', orgLogin?: string) => void
} & SxProp
const AssigneesSection = ({
  sectionHeader,
  onSelfAssignClick,
  assignees,
  readonly,
  copilotAssignmentButton,
  copilotEligibility,
  owner,
  businessSlug,
  onEligibilityUpdate,
  onRequestSuccess,
}: AssigneesSectionProps) => {
  // Feature flag check for Copilot coding agent member feature request eligibility
  // eslint-disable-next-line @github-ui/github-monorepo/no-use-feature-flags
  const ccaFeatureRequestEnabled = useFeatureFlag('cca_feature_request_issue_assignment')
  // Feature flag check for allowing user to remove Copilot coding agent feature request
  // eslint-disable-next-line @github-ui/github-monorepo/no-use-feature-flags
  const ccaFeatureRequestRemoveFlowEnabled = useFeatureFlag('cca_feature_request_issue_assignment_remove_flow')
  const [dialogDismissed] = useLocalStorage(HIDE_CCA_REQUEST_DIALOG_KEY, false)
  const [actionTaken, setActionTaken] = useState(false)

  const handleRequestSuccess = useCallback(
    (actionType?: 'request' | 'enable') => {
      setActionTaken(true)
      onRequestSuccess?.(actionType, owner)
    },
    [onRequestSuccess, owner],
  )

  const shouldShowCopilotRequestOrEnableLink =
    ccaFeatureRequestEnabled &&
    !dialogDismissed &&
    copilotEligibility &&
    !actionTaken &&
    (copilotEligibility.canRequestOrGrantCopilotAgent ||
      (ccaFeatureRequestRemoveFlowEnabled && copilotEligibility.copilotAgentEligibility.hasRequestedCca))

  const emptyHeader = !readonly ? (
    <>
      {LABELS.emptySections.assignees(true)}
      <Button variant="link" onClick={onSelfAssignClick} className={styles.selfAssignButton}>
        {LABELS.emptySections.selfAssign}
      </Button>
      {shouldShowCopilotRequestOrEnableLink && (
        <>
          {' or '}
          <Button
            variant="link"
            onClick={copilotEligibility.openRequestAccessDialog}
            className={styles.selfAssignButton}
          >
            {copilotEligibility.copilotAgentEligibility.canGrantOrgCca ||
            copilotEligibility.copilotAgentEligibility.canGrantEnterpriseCca
              ? 'enable coding agent'
              : 'request coding agent'}
          </Button>
        </>
      )}
    </>
  ) : (
    LABELS.emptySections.assignees(false)
  )

  // Wrap the copilot assignment button to match the styling of other section buttons like Unsubscribe
  const sectionFooter = copilotAssignmentButton ? (
    <div className="width-full d-flex flex-column flex-items-stretch pt-2 px-2">
      <div>{copilotAssignmentButton}</div>
    </div>
  ) : undefined

  // convert assignees of type AssigneePickerAssignee to Assignee
  const assigneeList = assignees.map(assignee => ({
    ...assignee,
    displayName: assignee.displayName || assignee.login,
    isCopilot: assignee.isCopilot ?? false,
    isAgent: assignee.isAgent ?? false,
  }))

  return (
    <>
      <Section
        id={`sidebar-assignees-section`}
        sectionHeader={sectionHeader}
        sectionFooter={sectionFooter}
        emptyText={assignees.length > 0 ? undefined : emptyHeader}
      >
        <Assignees assignees={assigneeList} testId={TEST_IDS.assignees} />
      </Section>
      {copilotEligibility?.requestAccessDialogOpen && onEligibilityUpdate && onRequestSuccess && (
        <CopilotCodingAgentRequestAccessDialog
          onClose={copilotEligibility.closeRequestAccessDialog}
          eligibility={copilotEligibility.copilotAgentEligibility}
          orgLogin={owner || ''}
          businessSlug={businessSlug ?? undefined}
          onEligibilityUpdate={onEligibilityUpdate}
          onRequestSuccess={handleRequestSuccess}
        />
      )}
    </>
  )
}

export type CreateIssueAssigneesSectionProps = {
  repo: string
  owner: string
  readonly: boolean
  assignees: AssigneePickerAssignee[]
  onSelectionChange: (value: AssigneePickerAssignee[]) => void
  insidePortal: boolean
  className?: string
  repository?: CopilotAssignmentRepository // Repository object for Copilot assignment
  issueNumber?: number
  onIssueUpdate?: () => void
} & SxProp
export function CreateIssueAssigneesSection({
  repo,
  owner,
  readonly,
  assignees,
  onSelectionChange,
  className,
  sx,
  repository,
  issueNumber,
  onIssueUpdate,
  ...sharedConfigProps
}: CreateIssueAssigneesSectionProps) {
  const viewer = useViewer()

  const pickerProps = {
    repo,
    owner,
    readonly,
    includeAuthorableBots: false,
    includeAssignableBots: true,
    assigneeTokens: [],
    assignees,
    onSelectionChange,
    anchorElement: (anchorProps: React.HTMLAttributes<HTMLElement>, ref?: RefObject<HTMLButtonElement | null>) => (
      <SectionHeader
        title={LABELS.sectionTitles.assignees}
        buttonProps={{...anchorProps, id: 'assignees-select-menu'}}
        ref={ref}
      />
    ),
    ...sharedConfigProps,
  }

  const sectionHeader = readonly ? <ReadonlyAssigneesSectionHeader /> : <AssigneeRepositoryPicker {...pickerProps} />

  return (
    <AssigneesSection
      sectionHeader={sectionHeader}
      onSelfAssignClick={() => onSelectionChange(viewer ? [viewer] : [])}
      assignees={assignees}
      readonly={readonly}
      sx={sx}
      className={className}
    />
  )
}

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
export const assigneesSectionAssignees = graphql`
  fragment AssigneesSectionAssignees on Issue {
    assignedActors(first: 20) {
      nodes {
        ... on Actor {
          __typename
          id
          login
          name
          profileResourcePath
          avatarUrl(size: 64)
          ... on Bot {
            isCopilot
            isAgent
            displayName
          }
        }
      }
    }
  }
`

// Fragment for repository data needed by CopilotAssignmentModal
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
export const assigneesSectionCopilotRepositoryFragment = graphql`
  fragment AssigneesSectionCopilotRepositoryFragment on Repository {
    # Used to determine whether to render Copilot modal
    isArchived
    # Checks if Assign to Copilot should be shown
    isCopilotAgentEnabled
  }
`

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
export const assigneesSectionFragment = graphql`
  fragment AssigneesSectionFragment on Issue {
    id
    number
    repository {
      name
      owner {
        login
      }
      isArchived
      ...AssigneesSectionCopilotRepositoryFragment
    }
    ...AssigneesSectionAssignees
    viewerCanAssign
  }
`

export type EditIssueAssigneesSectionProps = {
  issue: AssigneesSectionFragment$key
  viewer: AssigneesSectionViewer$key | null
  lazyKey?: AssigneesSectionLazyFragment$key
  onIssueUpdate?: () => void
  insideSidePanel?: boolean
  readonly: boolean
  onRequestSuccess?: (actionType?: 'request' | 'enable', orgLogin?: string) => void
}
export function EditIssueAssigneesSection({
  issue,
  viewer,
  lazyKey,
  onIssueUpdate,
  insideSidePanel,
  readonly,
  onRequestSuccess,
}: EditIssueAssigneesSectionProps) {
  const {sendAnalyticsEvent} = useAnalytics()
  const report_sidebar_interactions = isFeatureEnabled('issues_report_sidebar_interactions')
  const data = useFragment(assigneesSectionFragment, issue)
  const {
    repository,
    repository: {
      owner: {login: owner},
      name: repo,
      isArchived: isRepositoryArchived,
    },
    number,
  } = data

  const copilotRepositoryData = useFragment<AssigneesSectionCopilotRepositoryFragment$key>(
    assigneesSectionCopilotRepositoryFragment,
    data.repository,
  )

  const assigneesData = useFragment<AssigneesSectionAssignees$key>(assigneesSectionAssignees, data)

  const viewerData = useFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment AssigneesSectionViewer on Actor {
        __typename
        id
        login
        name
        profileResourcePath
        avatarUrl(size: 64)
        ... on Bot {
          isCopilot
          isAgent
          displayName
        }
      }
    `,
    viewer,
  )

  const {id: issueId, viewerCanAssign} = data
  const lazyData = useFragment(AssigneesSectionLazyFragment, lazyKey)

  const suggestions = useMemo(() => {
    return (lazyData?.suggestedActors?.nodes || []).flatMap(actor => (actor ? [actor] : [])) as AssigneePickerAssignee[]
  }, [lazyData?.suggestedActors?.nodes])

  const assignees = useMemo(() => {
    return (assigneesData.assignedActors?.nodes || []).flatMap(actor =>
      actor ? [actor] : [],
    ) as AssigneePickerAssignee[]
  }, [assigneesData.assignedActors?.nodes])

  const {addToast} = useToastContext()
  const environment = useRelayEnvironment()
  const copilotButtonRef = useRef<HTMLButtonElement>(null)

  const copilotCodingAgentEligibility = {
    hasRequestedCca: lazyData?.repository?.viewerCopilotCodingAgentEligibility?.hasRequestedCca ?? false,
    canRequestCca: lazyData?.repository?.viewerCopilotCodingAgentEligibility?.canRequestCca ?? false,
    canGrantOrgCca: lazyData?.repository?.viewerCopilotCodingAgentEligibility?.canGrantOrgCca ?? false,
    canGrantEnterpriseCca: lazyData?.repository?.viewerCopilotCodingAgentEligibility?.canGrantEnterpriseCca ?? false,
    businessSlug: lazyData?.repository?.owner?.enterprise?.slug ?? null,
  }

  const copilotEligibility = useCopilotCodingAgentEligibility(copilotCodingAgentEligibility)

  const assignActorWithAgentEnabled = isFeatureEnabled('issues_cca_assign_actor_with_agent')

  const readonlyAssignee = readonly || !viewerCanAssign || isRepositoryArchived

  const canUseCopilotAssignment = useMemo(() => {
    const isCopilotCurrentlyAssigned = assignees.some(assignee => isCopilot(assignee.login))
    return !readonlyAssignee && copilotRepositoryData.isCopilotAgentEnabled && !isCopilotCurrentlyAssigned
  }, [assignees, readonlyAssignee, copilotRepositoryData.isCopilotAgentEnabled])

  // State for managing Copilot modal triggered from both button and assignee picker
  const [copilotModalOpen, setCopilotModalOpen] = useState(false)
  const [pickerInstanceKey, setPickerInstanceKey] = useState(0)

  const handleOpenCopilotModal = useCallback(() => {
    setCopilotModalOpen(true)
    /* Force a remount of the picker so it resets to initialSelectedItems (equivalent to Cancel)
      This is needed because when we do nothing with Copilot, it remains selected in the picker
      and the modal would reopen immediately when the picker opens */
    setPickerInstanceKey(prev => prev + 1)
  }, [setCopilotModalOpen])

  const handleCloseCopilotModal = useCallback(() => {
    setCopilotModalOpen(false)
  }, [setCopilotModalOpen])

  const updateAssignees = useCallback(
    (selectedAssignees: AssigneePickerAssignee[]) => {
      commitUpdateIssueAssigneesMutation({
        environment,
        input: {issueId, assignedActors: selectedAssignees, participants: suggestions},
        onError: () =>
          // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
          addToast({
            type: 'error',
            message: ERRORS.couldNotUpdateAssignees,
          }),
        onCompleted: onIssueUpdate,
      })
    },
    [addToast, environment, issueId, onIssueUpdate, suggestions],
  )

  const updateAssigneesWithCopilotDetection = useCallback(
    (selectedAssignees: AssigneePickerAssignee[]) => {
      // Calculate if Copilot was previously assigned
      const wasCopilotPreviouslyAssigned = assignees.some(assignee => isCopilot(assignee.login))

      // Check if Copilot is in the selection and feature is enabled
      const hasCopilotAssignee = selectedAssignees.some(assignee => assignee.isCopilot)
      const nonCopilotAssignees = selectedAssignees.filter(assignee => !assignee.isCopilot)

      let assigneesToSave = canUseCopilotAssignment ? nonCopilotAssignees : selectedAssignees

      // Add viewer to assignees when a 3rd party agent is being added
      if (assignActorWithAgentEnabled && viewerData) {
        // Check if a 3rd party agent (non-Copilot) is being added
        const previousAgentLogins = new Set(assignees.filter(a => a.isAgent && !isCopilot(a.login)).map(a => a.login))
        const isThirdPartyAgentBeingAdded = selectedAssignees.some(
          assignee => assignee.isAgent && !isCopilot(assignee.login) && !previousAgentLogins.has(assignee.login),
        )

        if (isThirdPartyAgentBeingAdded) {
          const isViewerAlreadyAssigned = assignees.some(assignee => assignee.login === viewerData.login)
          const isViewerInSelection = selectedAssignees.some(assignee => assignee.login === viewerData.login)

          const shouldAddViewer =
            !isViewerAlreadyAssigned && !isViewerInSelection && selectedAssignees.length < DEFAULT_MAXIMUM_ASSIGNEES

          if (shouldAddViewer) {
            assigneesToSave = [...assigneesToSave, viewerData as AssigneePickerAssignee]
          }
        }
      }

      // Only check for unnecessary mutations when Copilot assignment is enabled
      // When disabled, always save to maintain existing behavior
      if (canUseCopilotAssignment) {
        // Only save if the resulting assignees list is different from current assignees
        // This prevents unnecessary mutations when only Copilot is removed/added
        const currentNonCopilotAssignees = assignees.filter(assignee => !isCopilot(assignee.login))

        if (!assigneesEqual(assigneesToSave, currentNonCopilotAssignees)) {
          updateAssignees(assigneesToSave)
        }
      } else {
        updateAssignees(assigneesToSave)
      }

      // Only open modal if Copilot is being ADDED (not removed)
      // This checks: Copilot is in new selection AND was not previously assigned
      if (hasCopilotAssignee && canUseCopilotAssignment && !wasCopilotPreviouslyAssigned) {
        handleOpenCopilotModal()
      }
    },
    [
      assignees,
      canUseCopilotAssignment,
      updateAssignees,
      handleOpenCopilotModal,
      assignActorWithAgentEnabled,
      viewerData,
    ],
  )

  const onAssignmentComplete = useCallback(
    (copilot?: Copilot) => {
      // After successful Copilot assignment, directly update the Relay store to include Copilot and the viewer as assignees
      if (copilot) {
        commitLocalUpdate(environment, store => {
          const assigneesToAdd: AssigneePickerAssignee[] = [
            {
              ...copilot,
              isCopilot: true,
              __typename: 'Bot',
            },
          ]
          const isViewerAlreadyAssigned = assignees.some(assignee => assignee.login === viewerData?.login)
          const shouldAddViewer =
            assignActorWithAgentEnabled && !isViewerAlreadyAssigned && assignees.length + 1 < DEFAULT_MAXIMUM_ASSIGNEES
          if (viewerData && shouldAddViewer) {
            assigneesToAdd.push(viewerData as AssigneePickerAssignee)
          }
          addAssigneesToIssueLocally(store, issueId, assigneesToAdd)
        })
      }

      onIssueUpdate?.()
    },
    [assignActorWithAgentEnabled, assignees, environment, issueId, onIssueUpdate, viewerData],
  )

  const copilotAssignmentButton = canUseCopilotAssignment ? (
    <CopilotAssignmentButton onClick={handleOpenCopilotModal} ref={copilotButtonRef} />
  ) : undefined

  // Modal for Copilot assignment triggered from both button and assignee picker
  const copilotAssignmentModal = canUseCopilotAssignment ? (
    <CopilotAssignmentModal
      isOpen={copilotModalOpen}
      onClose={handleCloseCopilotModal}
      repository={{
        name: repository.name,
        owner: {
          login: repository.owner.login,
        },
      }}
      issueNumbers={[number]}
      onAssignmentComplete={onAssignmentComplete}
      returnFocusRef={copilotButtonRef}
    />
  ) : undefined

  const assigneeSectionHeader = useMemo(() => {
    if (readonlyAssignee) {
      return <ReadonlyAssigneesSectionHeader />
    }

    const pickerProps: AssigneeIssuePickerProps = {
      repo,
      owner,
      number,
      anchorElement: (props: React.HTMLAttributes<HTMLElement>, ref?: RefObject<HTMLButtonElement | null>) => (
        <SectionHeader
          title={LABELS.sectionTitles.assignees}
          buttonProps={{
            ...props,
            onClick: e => {
              if (report_sidebar_interactions) {
                sendAnalyticsEvent('analytics.click', 'ISSUE_SIDEBAR_ASSIGNEE_SECTION_EDIT_CLICK')
              }
              props.onClick?.(e)
            },
          }}
          ref={ref}
        />
      ),
      readonly: readonlyAssignee,
      assignees,
      onSelectionChange: (selectedAssignees: AssigneePickerAssignee[]) =>
        updateAssigneesWithCopilotDetection(selectedAssignees),
      insidePortal: insideSidePanel,
      suggestions,
    }

    return <AssigneePicker key={`assignee-picker-${pickerInstanceKey}`} {...pickerProps} />
  }, [
    readonlyAssignee,
    repo,
    owner,
    number,
    assignees,
    insideSidePanel,
    suggestions,
    pickerInstanceKey,
    report_sidebar_interactions,
    sendAnalyticsEvent,
    updateAssigneesWithCopilotDetection,
  ])

  return (
    <>
      <AssigneesSection
        sectionHeader={assigneeSectionHeader}
        assignees={assignees}
        onSelfAssignClick={() => (viewerData ? updateAssignees([viewerData as AssigneePickerAssignee]) : noop)}
        readonly={readonlyAssignee}
        copilotAssignmentButton={copilotAssignmentButton}
        copilotEligibility={copilotEligibility}
        owner={owner}
        businessSlug={copilotCodingAgentEligibility.businessSlug}
        onEligibilityUpdate={async () => {
          if (onIssueUpdate) {
            onIssueUpdate()
          }
        }}
        onRequestSuccess={onRequestSuccess}
      />
      {copilotAssignmentModal}
    </>
  )
}

try{ ReadonlyAssigneesSectionHeader.displayName ||= 'ReadonlyAssigneesSectionHeader' } catch {}
try{ AssigneesSection.displayName ||= 'AssigneesSection' } catch {}
try{ CreateIssueAssigneesSection.displayName ||= 'CreateIssueAssigneesSection' } catch {}
try{ EditIssueAssigneesSection.displayName ||= 'EditIssueAssigneesSection' } catch {}